import React, { CSSProperties, isValidElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import CrossFadeContainer from "components/CrossFadeContainer";
import { Sizes } from 'styles/sizes';
import { cx } from 'utils';


type CardSizes = Pick<Sizes<{ padding: string, gap: string, titleText: string }>, "SM" | "MD" | "LG" | "XL">;

const cardSizes: CardSizes = {
  SM: { padding: "p-3", gap: "gap-y-3", titleText: "text-sm" },
  MD: { padding: "p-4", gap: "gap-y-4", titleText: "text-base" },
  LG: { padding: "p-5", gap: "gap-y-5", titleText: "text-lg" },
  XL: { padding: "p-6", gap: "gap-y-6", titleText: "text-xl" },
};

export interface ICardProps {
  /**
   * Can be used for anchor links. If an ID is set for a component (like a <div> here), it acts as an anchor and
   * with links like /page#id the browser (or our <ScrollToAnchorLayout> component) will scroll there automatically.
   */
  id?: string;
  title?: string | ReactNode;
  extra?: ReactNode;
  actionHeader?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  size?: keyof CardSizes;
  hoverable?: boolean;
  segmented?: boolean;
  borderless?: boolean;
  horizontal?: boolean;
  animation?: {
    resize?: {
      duration?: number;
      delay?: number;
    },
    crossFade?: {
      duration?: number;
      delay?: number;
    }
  },
  contentKey?: string;
  fullHeight?: boolean;
}

const Card: React.FC<ICardProps> = ({
  id,
  children,
  className,
  size = "MD",
  hoverable = false,
  segmented = false,
  borderless = false,
  horizontal = false,
  fullHeight = false,
  animation,
  contentKey,
  ...contentProps
}) => {
  const sizeClass = cardSizes[size];

  const cardContainerRef = useRef<HTMLDivElement>(null);
  const cardContentRef = useRef<HTMLDivElement>(null);

  const [ cardHeight, setCardHeight ] = useState<CSSProperties["height"]>("auto");

  // animate height changes
  useLayoutEffect(() => {
    // do nothing if resize animation is disabled
    if (!animation?.resize?.duration && !animation?.resize?.delay) {
      setCardHeight("auto");
      return;
    }

    const cardContainer = cardContainerRef.current;
    const cardContent = cardContentRef.current;

    if (!cardContainer || !cardContent) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const newHeight = cardContent.getBoundingClientRect().height;
      setCardHeight(newHeight);
    });

    resizeObserver.observe(cardContent);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ children ]);


  return (
    <div
      id={ id }
      ref={ cardContainerRef }
      className={ cx(
        "overflow-hidden transition-[height] ease-in-out",
        id && "scroll-mt-4",  // adding a small gap so it's not exactly at the top of the browser view when scrolled. should be overridden for example when having sticky headers
        className,
        !borderless && (
          "rounded-md border border-neutral-200 " +

          // supporting cases when used inside a <CardStack>
          "group-[.flex]/card-stack:rounded-none " +
          // setting borders for horizontal layout
          "group-[.flex-row]/card-stack:border-r-0 " +
          "first:group-[.flex-row]/card-stack:border-l first:group-[.flex-row]/card-stack:rounded-l-md " +
          "last:group-[.flex-row]/card-stack:border-r last:group-[.flex-row]/card-stack:rounded-r-md " +

          // setting borders for vertical layout
          "group-[.flex-col]/card-stack:border-b-0 " +
          "first:group-[.flex-col]/card-stack:border-t first:group-[.flex-col]/card-stack:rounded-t-md " +
          "last:group-[.flex-col]/card-stack:border-b last:group-[.flex-col]/card-stack:rounded-b-md "
        )
      ) }
      style={ { height: cardHeight, transitionDuration: `${animation?.resize?.duration ?? 0}ms`, transitionDelay: `${animation?.resize?.delay ?? 0}ms`} }
    >
      <div
        ref={ cardContentRef }
        className={ cx(
          hoverable && "transition-colors duration-200 dark:hover:bg-accent-50/50",
          fullHeight && "h-full",
          "flex justify-between",
          horizontal ? "flex-row" : "flex-col",
          !borderless && sizeClass?.padding,
          sizeClass?.gap
        ) }
      >
        <CrossFadeIfNecessary animation={ animation}>
          <CardContent
            key={ contentKey ?? (isValidElement(children) ? children.key : undefined ) }
            { ...contentProps }
            segmented={ segmented }
            horizontal={ horizontal }
            size={ size }
          >
            { children }
          </CardContent>
        </CrossFadeIfNecessary>
      </div>
    </div>
  );
};

function CrossFadeIfNecessary({ animation, children }: Pick<ICardProps, "animation" | "children">) {
  if (!animation?.crossFade?.duration && !animation?.crossFade?.delay) {
    return (
      <>
        { children }
      </>
    );
  } else {
    return (
      <CrossFadeContainer { ...animation.crossFade }>
        { children }
      </CrossFadeContainer>
    );
  }
}

function CardContent(
  {
    title,
    extra,
    actionHeader,
    segmented,
    horizontal,
    footer,
    size = "MD",
    children,
  }: Pick<ICardProps, "title" | "extra" | "actionHeader" | "segmented" | "horizontal" | "footer" | "size" | "children">
) {
  const sizeClass = cardSizes[size];

  return (
    <>
      {(title || extra) && (
        <div className={cx(
          "flex justify-between items-center",
          segmented && "border-neutral-100",
          segmented && (horizontal ? "border-r" : "border-b")
        )}>
          {title && <h2 className={ cx("font-medium", sizeClass?.titleText || "text-lg") }>{title}</h2>}
          {extra && <div>{extra}</div>}
        </div>
      ) }
      {actionHeader && <div>{actionHeader}</div>}
      {children}
      {footer && (
        <div>{footer}</div>
      )}
    </>
  );
}


export default Card;
